import { useState } from "react";
import { Input, InputNumber, Modal, Space, Switch, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import type { GraphFormState } from "../plotState";
import type { ChartReferenceBand } from "../../../Processing/types";
import { BENCHMARK_TYPES } from "../../constants";
import { ColorSwatchPicker } from "../../shared/ColorSwatchPicker";
import {
  DEFAULT_REFERENCE_BAND_COLOR,
  REFERENCE_BAND_PRESET_COLORS,
} from "../../../Processing/referenceBands";
import { RefLineList, RefLineModal, REF_LINE_PRESET_COLORS } from "../../shared/RefLineEditor";

const { Text } = Typography;

const DEFAULT_REF_LINE_COLOR = "#ff4d4f";

interface AdditionalOptionsStepProps {
  graph: GraphFormState;
  onChange: (patch: Partial<GraphFormState>) => void;
  isPartOfStack?: boolean;
}

export function AdditionalOptionsStep({ graph, onChange, isPartOfStack }: AdditionalOptionsStepProps) {
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const openAddModal = () => {
    setEditingIndex(null);
    setCustomModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setCustomModalOpen(true);
  };

  const toggleBenchmark = (val: string) => {
    if (val === "custom") {
      openAddModal();
      return;
    }
    const next = graph.benchmarks.includes(val)
      ? graph.benchmarks.filter((v) => v !== val)
      : [...graph.benchmarks, val];
    onChange({ benchmarks: next });
  };

  const handleSaveCustom = (line: { value: number; label?: string; colorHex?: string }) => {
    if (editingIndex !== null) {
      const updated = graph.customRefLines.map((rl, i) => (i === editingIndex ? line : rl));
      onChange({ customRefLines: updated });
    } else {
      onChange({ customRefLines: [...graph.customRefLines, line] });
    }
    setCustomModalOpen(false);
  };

  const handleRemoveCustom = (index: number) => {
    onChange({ customRefLines: graph.customRefLines.filter((_, i) => i !== index) });
  };

  const [bandModalOpen, setBandModalOpen] = useState(false);
  const [editingBandIndex, setEditingBandIndex] = useState<number | null>(null);
  const [bandLower, setBandLower] = useState<number | null>(null);
  const [bandUpper, setBandUpper] = useState<number | null>(null);
  const [bandLabel, setBandLabel] = useState("");
  const [bandColor, setBandColor] = useState(DEFAULT_REFERENCE_BAND_COLOR);

  const openAddBandModal = () => {
    setEditingBandIndex(null);
    setBandLower(null);
    setBandUpper(null);
    setBandLabel("");
    setBandColor(
      REFERENCE_BAND_PRESET_COLORS[graph.referenceBands.length % REFERENCE_BAND_PRESET_COLORS.length].color,
    );
    setBandModalOpen(true);
  };

  const openEditBandModal = (index: number) => {
    const band = graph.referenceBands[index];
    setEditingBandIndex(index);
    setBandLower(band.lower);
    setBandUpper(band.upper);
    setBandLabel(band.label ?? "");
    setBandColor(band.colorHex ?? DEFAULT_REFERENCE_BAND_COLOR);
    setBandModalOpen(true);
  };

  const bandBoundsValid = bandLower != null && bandUpper != null && bandUpper > bandLower;
  const bandBoundsError =
    bandLower != null && bandUpper != null && bandUpper <= bandLower
      ? "The upper bound must be greater than the lower bound."
      : null;

  const handleSaveBand = () => {
    if (!bandBoundsValid) return;
    const band: ChartReferenceBand = {
      lower: bandLower as number,
      upper: bandUpper as number,
      label: bandLabel.trim() || undefined,
      colorHex: bandColor,
    };
    const next =
      editingBandIndex !== null
        ? graph.referenceBands.map((b, i) => (i === editingBandIndex ? band : b))
        : [...graph.referenceBands, band];
    onChange({ referenceBands: next, enableReferenceBands: true });
    setBandModalOpen(false);
  };

  const handleRemoveBand = (index: number) => {
    onChange({ referenceBands: graph.referenceBands.filter((_, i) => i !== index) });
  };

  return (
    <div className="awm-step-body">
      <Text type="secondary" style={{ fontSize: 12 }}>
        Add visual aids to make the chart easier to interpret.
      </Text>

      <div
        style={{
          padding: "8px 10px",
          border: `1px solid ${graph.customNameEnabled ? "#1677ff" : "#e0e5eb"}`,
          borderRadius: 8,
          background: graph.customNameEnabled ? "#f0f5ff" : "#fafbfc",
          display: "flex", flexDirection: "column", gap: 6,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Text strong style={{ fontSize: 12 }}>Override Plot Name</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Override the legend/tooltip label for this plot. Replaces the auto-generated
              text (aggregate prefix, athlete subjects, etc.).
            </Text>
          </div>
          <Switch
            size="small"
            checked={graph.customNameEnabled}
            onChange={(v) =>
              onChange({
                customNameEnabled: v,
                ...(!v && { customName: "" }),
              })
            }
          />
        </div>
        {graph.customNameEnabled && (
          <Input
            size="small"
            placeholder="e.g. Match-Day Player Load"
            value={graph.customName}
            onChange={(e) => onChange({ customName: e.target.value })}
            maxLength={80}
          />
        )}
      </div>

      <div
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
        }}
      >
        <div>
          <Text strong style={{ fontSize: 12 }}>Trendline</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Overlay a best-fit line to show the general direction.
          </Text>
        </div>
        <Switch
          size="small"
          checked={graph.trendline}
          onChange={(v) => onChange({ trendline: v })}
        />
      </div>

      {graph.chartType === "line" && (
        <div
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
          }}
        >
          <div>
            <Text strong style={{ fontSize: 12 }}>Curved Line</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Render as a smooth spline instead of straight line segments.
            </Text>
          </div>
          <Switch
            size="small"
            checked={graph.curvedLine}
            onChange={(v) => onChange({ curvedLine: v })}
          />
        </div>
      )}

      {graph.chartType === "line" && (
        <div
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
          }}
        >
          <div>
            <Text strong style={{ fontSize: 12 }}>Shaded Area</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Shade the area under the line with the line color.
            </Text>
          </div>
          <Switch
            size="small"
            checked={graph.areaFill}
            onChange={(v) => onChange({ areaFill: v })}
          />
        </div>
      )}

      <div
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
        }}
      >
        <div>
          <Text strong style={{ fontSize: 12 }}>Data Labels</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Show data values as text labels on each data point.
          </Text>
        </div>
        <Switch
          size="small"
          checked={graph.barLabels}
          onChange={(v) => onChange({ barLabels: v })}
        />
      </div>

      {graph.chartType === "bar" && isPartOfStack && (
        <div
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
            ...(graph.enableColorOverride && { opacity: 0.45, pointerEvents: "none" }),
          }}
        >
          <div>
            <Text strong style={{ fontSize: 12 }}>Stacked Bar Coloring</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Use the platform&apos;s standard zone coloring for bars in this stack.
            </Text>
          </div>
          <Switch
            size="small"
            checked={graph.stackedBarColoring}
            disabled={graph.enableColorOverride}
            onChange={(v) => onChange({ stackedBarColoring: v, ...(v && { enableColorOverride: false }) })}
          />
        </div>
      )}

      <div
        style={{
          padding: "8px 10px",
          border: `1px solid ${graph.enableColorOverride ? "#1677ff" : "#e0e5eb"}`,
          borderRadius: 8,
          background: graph.enableColorOverride ? "#f0f5ff" : "#fafbfc",
          display: "flex", flexDirection: "column", gap: 6,
          ...(graph.stackedBarColoring && { opacity: 0.45, pointerEvents: "none" }),
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Text strong style={{ fontSize: 12 }}>Override Color</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Use a custom color instead of the default palette.
            </Text>
          </div>
          <Switch
            size="small"
            checked={graph.enableColorOverride}
            disabled={graph.stackedBarColoring}
            onChange={(checked) => {
              onChange({
                enableColorOverride: checked,
                ...(checked && !graph.colorOverride && { colorOverride: "#1677ff" }),
                ...(!checked && { colorOverride: "" }),
              });
            }}
          />
        </div>
        {graph.enableColorOverride && (
          <ColorSwatchPicker
            color={graph.colorOverride || ""}
            presetColors={REF_LINE_PRESET_COLORS}
            onChange={(hex) => onChange({ colorOverride: hex })}
          />
        )}
      </div>

      <div
        style={{
          padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8,
          background: "#fafbfc", display: "flex", flexDirection: "column", gap: 4,
        }}
      >
        <Text strong style={{ fontSize: 12 }}>Reference Lines</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Add horizontal benchmark lines for this plot. Athlete Baseline uses the
          baselines of this plot&apos;s athletes.
        </Text>
        <Space size={5} wrap style={{ marginTop: 2 }}>
          {BENCHMARK_TYPES.filter((b) => b.value !== "custom").map((b) => {
            const isAthleteBaseline = b.value === "athleteBaseline";
            // A count has no baseline — it is kept out of the metric list the baseline
            // resolver reads, so the line would silently fail to draw. The category
            // check alone does not catch it: picking a count from the dropdown leaves
            // activeCategory on whatever it was, so a GPS author keeps the tag enabled.
            const disabledForCount = isAthleteBaseline && !!graph.countSpec;
            const disabledForCategory =
              (isAthleteBaseline && graph.activeCategory !== "GPS") || disabledForCount;
            return (
              <Tooltip
                key={b.value}
                title={
                  disabledForCount
                    ? "A count has no athlete baseline to measure against"
                    : disabledForCategory
                      ? "Athlete Baseline is only available for GPS metrics"
                      : undefined
                }
              >
                <Tag.CheckableTag
                  checked={graph.benchmarks.includes(b.value)}
                  onChange={() => !disabledForCategory && toggleBenchmark(b.value)}
                  style={disabledForCategory ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                >
                  {b.label}
                </Tag.CheckableTag>
              </Tooltip>
            );
          })}
          <Tag
            style={{ cursor: "pointer", borderStyle: "dashed" }}
            onClick={openAddModal}
          >
            + Custom
          </Tag>
        </Space>

        <RefLineList
          lines={graph.customRefLines}
          defaultColor={DEFAULT_REF_LINE_COLOR}
          onEdit={openEditModal}
          onRemove={handleRemoveCustom}
        />
      </div>

      <div
        style={{
          padding: "8px 10px",
          border: `1px solid ${graph.enableReferenceBands ? "#1677ff" : "#e0e5eb"}`,
          borderRadius: 8,
          background: graph.enableReferenceBands ? "#f0f5ff" : "#fafbfc",
          display: "flex", flexDirection: "column", gap: 4,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Text strong style={{ fontSize: 12 }}>Reference Bands</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Shade one or more value ranges behind this plot, such as a target zone.
            </Text>
          </div>
          <Switch
            size="small"
            checked={graph.enableReferenceBands}
            onChange={(v) => onChange({ enableReferenceBands: v })}
          />
        </div>
        {graph.enableReferenceBands && (
          <>
            {graph.referenceBands.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 2 }}>
                {graph.referenceBands.map((band, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "3px 8px", background: "#fff", border: "1px solid #e8ecf0",
                      borderRadius: 6, fontSize: 12,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div
                        style={{
                          width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                          background: band.colorHex ?? DEFAULT_REFERENCE_BAND_COLOR,
                          border: "1px solid rgba(0,0,0,0.15)",
                        }}
                      />
                      <span style={{ color: "#333", fontWeight: 500 }}>
                        {band.lower} – {band.upper}
                        {band.label && (
                          <span style={{ color: "#888", fontWeight: 400, marginLeft: 6 }}>
                            {band.label}
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <EditOutlined
                        style={{ color: "#999", cursor: "pointer", fontSize: 12 }}
                        onClick={() => openEditBandModal(i)}
                      />
                      <DeleteOutlined
                        style={{ color: "#999", cursor: "pointer", fontSize: 12 }}
                        onClick={() => handleRemoveBand(i)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Space size={5} wrap style={{ marginTop: 2 }}>
              <Tag
                style={{ cursor: "pointer", borderStyle: "dashed" }}
                onClick={openAddBandModal}
              >
                + Add Band
              </Tag>
            </Space>
          </>
        )}
      </div>

      <Modal
        open={bandModalOpen}
        title={editingBandIndex !== null ? "Edit Reference Band" : "Add Reference Band"}
        onOk={handleSaveBand}
        onCancel={() => setBandModalOpen(false)}
        okText={editingBandIndex !== null ? "Save" : "Add"}
        okButtonProps={{ disabled: !bandBoundsValid }}
        destroyOnClose
        width={360}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 12 }}>Lower bound</Text>
              <InputNumber
                autoFocus
                style={{ width: "100%", marginTop: 4 }}
                placeholder="e.g. 200"
                value={bandLower}
                status={bandBoundsError ? "error" : undefined}
                onChange={(v) => setBandLower(v)}
                onPressEnter={handleSaveBand}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 12 }}>Upper bound</Text>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                placeholder="e.g. 300"
                value={bandUpper}
                status={bandBoundsError ? "error" : undefined}
                onChange={(v) => setBandUpper(v)}
                onPressEnter={handleSaveBand}
              />
            </div>
          </div>
          {bandBoundsError && (
            <Text type="danger" style={{ fontSize: 11 }}>{bandBoundsError}</Text>
          )}
          <div>
            <Text strong style={{ fontSize: 12 }}>Name <Text type="secondary">(optional)</Text></Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Shown in this list only, to tell the bands apart. It is not drawn on the chart.
            </Text>
            <Input
              size="small"
              style={{ marginTop: 4 }}
              placeholder="e.g. Target zone"
              value={bandLabel}
              maxLength={40}
              onChange={(e) => setBandLabel(e.target.value)}
              onPressEnter={handleSaveBand}
            />
          </div>
          <div>
            <Text strong style={{ fontSize: 12 }}>Color</Text>
            <div style={{ marginTop: 4 }}>
              <ColorSwatchPicker
                color={bandColor}
                presetColors={REFERENCE_BAND_PRESET_COLORS}
                fallbackColor={DEFAULT_REFERENCE_BAND_COLOR}
                onChange={setBandColor}
              />
            </div>
          </div>
        </div>
      </Modal>

      <RefLineModal
        open={customModalOpen}
        initial={editingIndex !== null ? graph.customRefLines[editingIndex] : null}
        defaultColor={DEFAULT_REF_LINE_COLOR}
        onSave={handleSaveCustom}
        onCancel={() => setCustomModalOpen(false)}
      />
    </div>
  );
}
